import {
    Avatar,
    Box,
    Button,
    Card,
    Chip,
    Divider,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import { FaHandshake } from "react-icons/fa";
import { FiArrowUpRight } from "react-icons/fi";
import useWindowSize from "../../../../hooks/useWindowSize";
import formatValues from "../../../../scripts/formatValues";

const headCellSx = {
    fontWeight: 700,
    fontSize: 13,
    color: "#fff",
    backgroundColor: "var(--color-primary-background)",
    whiteSpace: "nowrap",
};

function getInitials(name = "") {
    return name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((word) => word[0])
        .join("")
        .toUpperCase();
}

function DueThisMonthLabel({ agent }) {
    if (!agent.status) {
        return (
            <Tooltip title="Comissionado inativo — vínculos ativos não entram no total">
                <Typography variant="body2" color="text.secondary">—</Typography>
            </Tooltip>
        );
    }

    return <Typography variant="body2" fontWeight={600}>{formatValues(agent.dueThisMonth)}</Typography>;
}

function InfoRow({ label, value }) {
    return (
        <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={2}>
            <Typography variant="body2" color="text.secondary">{label}</Typography>
            <Typography variant="body2" fontWeight={600} noWrap>{value}</Typography>
        </Stack>
    );
}

export default function AgentsList({ agents, openAgent, openCompanies }) {
    const width = useWindowSize();
    const isDesktop = width >= 1280;

    if (isDesktop) {
        return (
            <TableContainer
                component={Paper}
                elevation={0}
                sx={{ borderRadius: 3, border: "1px solid #e3e6ea", maxHeight: "79vh" }}
            >
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ ...headCellSx, width: 56 }} />
                            <TableCell sx={{ ...headCellSx, width: 56 }} />
                            <TableCell sx={headCellSx} align="center">Nome</TableCell>
                            <TableCell sx={headCellSx} align="center">Telefone</TableCell>
                            <TableCell sx={headCellSx} align="center">Email</TableCell>
                            <TableCell sx={headCellSx} align="center">Status</TableCell>
                            <TableCell sx={headCellSx} align="center">Empresas ativas</TableCell>
                            <TableCell sx={headCellSx} align="center">A receber este mês</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {agents.map((item, index) => (
                            <TableRow
                                key={item.id ?? index}
                                hover
                                sx={{
                                    backgroundColor: index % 2 === 0 ? "#fff" : "#fafbfc",
                                    "&:last-child td": { borderBottom: 0 },
                                }}
                            >
                                <TableCell>
                                    <Tooltip title="Abrir comissionado">
                                        <IconButton
                                            size="small"
                                            onClick={() => openAgent(item)}
                                            sx={{
                                                color: "var(--color-primary-background)",
                                                border: "1px solid var(--color-primary-background)",
                                                "&:hover": {
                                                    backgroundColor: "var(--color-primary-background)",
                                                    color: "#fff",
                                                },
                                            }}
                                        >
                                            <FiArrowUpRight size={16} />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>

                                <TableCell>
                                    <Tooltip title="Ver empresas vinculadas">
                                        <IconButton
                                            size="small"
                                            onClick={() => openCompanies(item)}
                                            sx={{
                                                color: "var(--color-secondary-background)",
                                                border: "1px solid var(--color-secondary-background)",
                                                "&:hover": {
                                                    backgroundColor: "var(--color-secondary-background)",
                                                    color: "#fff",
                                                },
                                            }}
                                        >
                                            <FaHandshake size={14} />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>

                                <TableCell>
                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                        <Avatar
                                            sx={{
                                                width: 32,
                                                height: 32,
                                                fontSize: 13,
                                                bgcolor: "var(--color-primary-background)",
                                            }}
                                        >
                                            {getInitials(item.name)}
                                        </Avatar>
                                        <Typography variant="body2" fontWeight={600}>{item.name}</Typography>
                                    </Stack>
                                </TableCell>

                                <TableCell>
                                    <Typography variant="body2" color="text.secondary">{item.phone || "Sem dados"}</Typography>
                                </TableCell>

                                <TableCell>
                                    <Typography variant="body2" color="text.secondary">{item.email || "Sem dados"}</Typography>
                                </TableCell>

                                <TableCell align="center">
                                    <Chip label={item.status ? "Ativo" : "Inativo"} color={item.status ? "success" : "default"} size="small" />
                                </TableCell>

                                <TableCell align="center">
                                    <Typography variant="body2">{item.activeCompaniesCount}</Typography>
                                </TableCell>

                                <TableCell align="center">
                                    <DueThisMonthLabel agent={item} />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    }

    return (
        <Stack spacing={2}>
            {agents.map((item, index) => (
                <Card key={item.id ?? index} variant="outlined" sx={{ borderRadius: 3, p: 2 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                        <Avatar sx={{ bgcolor: "var(--color-primary-background)" }}>
                            {getInitials(item.name)}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="subtitle1" fontWeight={700} noWrap>{item.name}</Typography>
                            <Typography variant="body2" color="text.secondary" noWrap>{item.phone || "Sem telefone"}</Typography>
                            <Typography variant="body2" color="text.secondary" noWrap>{item.email || "Sem email"}</Typography>
                        </Box>
                    </Stack>

                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
                        <Chip label={item.status ? "Ativo" : "Inativo"} color={item.status ? "success" : "default"} size="small" />
                    </Stack>

                    <Divider sx={{ my: 1.5 }} />

                    <Stack spacing={1}>
                        <InfoRow label="Empresas ativas" value={item.activeCompaniesCount} />
                        <InfoRow label="A receber este mês" value={item.status ? formatValues(item.dueThisMonth) : "—"} />
                    </Stack>

                    <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                        <Button
                            fullWidth
                            endIcon={<FiArrowUpRight />}
                            onClick={() => openAgent(item)}
                            sx={{
                                backgroundColor: "var(--color-primary-background)",
                                color: "#fff",
                                "&:hover": { backgroundColor: "var(--color-secondary-background)" },
                            }}
                        >
                            Abrir
                        </Button>

                        <Button
                            fullWidth
                            endIcon={<FaHandshake />}
                            onClick={() => openCompanies(item)}
                            sx={{
                                backgroundColor: "var(--color-secondary-background)",
                                color: "#fff",
                                "&:hover": { backgroundColor: "var(--color-primary-background)" },
                            }}
                        >
                            Empresas
                        </Button>
                    </Stack>
                </Card>
            ))}
        </Stack>
    );
}
